import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Handing a customer their own record. §6.2 `CUS-006`, `CUS-006a`; §15.10 steps 1 and 2.
 *
 * **`CUS-006` is two tools and only erasure had a screen**, so a shop could erase a customer and
 * had no way to give them what it holds. §15.10 puts access and portability before erasure in the
 * flow, which is the order a real request arrives in: people ask what you have before they ask you
 * to delete it.
 *
 * **Two buttons, because `CUS-006a` describes two different acts.** An ordinary export is filtered
 * by what the person running it may view, and the file says what it left out. A subject-access
 * request is the customer's own data and is not filtered, and the words say that the person
 * operating the tool is not the person receiving the file. The second asks for the request to be
 * described, because the audit row is the only thing standing behind an unfiltered export.
 *
 * **The download is injected**, for the reason `download.ts` records: jsdom cannot observe a saved
 * file, so a screen calling it directly would have an untestable branch at the end of its most
 * important flow.
 */

export interface CustomerExportProps {
  readonly customerId: string
  readonly customerName: string
  /** Appendix B's `Customer - export`. */
  readonly canExport: boolean
  /** `download.ts`, passed in so a test can see the filename and the bytes. */
  readonly offerDownload: (filename: string, base64: string, contentType?: string) => void
}

export function CustomerExport({
  customerId,
  customerName,
  canExport,
  offerDownload,
}: CustomerExportProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [withheld, setWithheld] = useState<readonly string[]>([])
  const [done, setDone] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    const answer = (
      typeof write.response === 'object' && write.response !== null ? write.response : {}
    ) as {
      filename?: unknown
      fileBase64?: unknown
      contentType?: unknown
      withheld?: unknown
    }
    if (typeof answer.filename === 'string' && typeof answer.fileBase64 === 'string') {
      offerDownload(
        answer.filename,
        answer.fileBase64,
        typeof answer.contentType === 'string' ? answer.contentType : 'application/json',
      )
    }
    // `CUS-006a`: what was left out, shown rather than only written into the file. The person
    // running it has to know their archive is partial before they hand it to anybody.
    setWithheld(
      (Array.isArray(answer.withheld) ? answer.withheld : [])
        .map((row) => (row as { section?: unknown })?.section)
        .filter((section): section is string => typeof section === 'string'),
    )
    setAsking(false)
    setReason('')
    setDone(true)
  })

  const run = useCallback(
    async (onBehalfOfCustomer: boolean) => {
      if (onBehalfOfCustomer && reason.trim() === '') {
        // The server refuses this too, and a refusal stops the write queue.
        setError(t('customerExport.reasonRequired'))
        return
      }
      setError(null)
      setDone(false)
      setWithheld([])
      const write = (await enqueue({
        method: 'POST',
        path: `/customers/${encodeURIComponent(customerId)}/export`,
        label: t('customerExport.queueLabel', { name: customerName }),
        body: onBehalfOfCustomer
          ? { onBehalfOfCustomer: true, reason: reason.trim() }
          : // Absent rather than false: the server reads the flag's absence as the ordinary
            // filtered export, and sending `false` says the same thing in a way somebody reading
            // the audit row has to think about.
            {},
      })) as { id: string }
      setWriteId(write.id)
    },
    [customerId, customerName, enqueue, reason, t],
  )

  if (!canExport) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.export'))

  return (
    <section className="lm-customer-export" aria-labelledby="customer-export-heading">
      <h2 id="customer-export-heading" className="lm-customer-export__heading">
        {t('customerExport.heading')}
      </h2>
      <p className="lm-customer-export__effects">{t('customerExport.whatItIs')}</p>

      {done ? (
        withheld.length === 0 ? (
          <p className="lm-customer-export__effects">{t('customerExport.done')}</p>
        ) : (
          <>
            <p className="lm-customer-export__effects">{t('customerExport.partial')}</p>
            <ul className="lm-customer-export__withheld">
              {withheld.map((section) => (
                <li key={section}>{t('customerExport.withheldSection', { section })}</li>
              ))}
            </ul>
          </>
        )
      ) : null}

      {error === null ? null : <p className="lm-error">{error}</p>}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {asking ? (
        <>
          <p className="lm-customer-export__effects">{t('customerExport.subjectAccessMeans')}</p>
          <label className="lm-field">
            <span className="lm-field__label">{t('customerExport.reasonLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              value={reason}
              autoComplete="off"
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={writeId !== null}
            onClick={() => {
              void run(true)
            }}
          >
            {t('customerExport.subjectAccessConfirm')}
          </button>
          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              setAsking(false)
              setReason('')
              setError(null)
            }}
          >
            {t('customerExport.cancel')}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="lm-button"
            disabled={writeId !== null}
            onClick={() => {
              void run(false)
            }}
          >
            {t('customerExport.forShop')}
          </button>
          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              setAsking(true)
              setDone(false)
            }}
          >
            {t('customerExport.forCustomer')}
          </button>
        </>
      )}
    </section>
  )
}
